// WHAT: Reading step component with notes textarea for capturing reading insights
// WHY:  Provides interface for users to document their reading experience and key takeaways
// DOMAIN: Productivity → Miracle Flow → Session Steps
// DEPENDENCIES: Textarea component, Stack layout, React controlled inputs
// RISKS:
//   - NOTE: Textarea value is controlled by parent - may lose data on unmount
//   - NOTE: No validation for empty notes - users may skip this step
//   - NOTE: No character limit - may cause performance issues with very long text

import { BookOpen } from "lucide-react";
import React from "react";

import { Stack, Heading, Textarea } from "@/components/ui";
import { cn } from "@/shared/utils";

export interface ReadingStepProperties {
  className?: string;
  onChange: (value: string) => void;
  value: string;
}

/**
 * WHAT:
 *   Reading step component with notes textarea for capturing reading insights and takeaways.
 *
 * WHY:
 *   Provides interface for users to document their reading experience and key learnings during session.
 *
 * PROPS:
 *   - value: string — Current reading notes text (controlled by parent)
 *   - onChange: (value: string) => void — Callback for text changes
 *   - className?: string — Optional CSS classes for styling
 *
 * BEHAVIOR:
 *   - Displays large textarea for reading notes input
 *   - Uses orange color scheme to represent knowledge/learning
 *   - Provides placeholder text with guidance on what to write
 *   - Shows helper text below textarea
 *   - All state is controlled by parent component
 *
 * RISKS:
 *   - NOTE: Textarea value is controlled by parent - may lose data on unmount if not saved
 *   - NOTE: No validation for empty notes - users may skip this step
 *   - NOTE: No character limit - may cause performance issues with very long text
 *   - WARN: No auto-save mechanism - data loss risk on navigation
 *
 * @param root0
 *
 * @param root0.value
 *
 * @param root0.onChange
 *
 * @param root0.className
 *
 * @returns JSX.Element
 *
 */
export const ReadingStep: React.FC<ReadingStepProperties> = ({ className, onChange, value }) => {
  return (
    <div className={cn("space-y-6 w-full", className)}>
      {/* Hero Section */}
      <Stack align="center" className="py-4 w-full" direction="col" gap={4}>
        <div className="flex items-center justify-center w-20 h-20 rounded-tracq bg-gradient-to-br from-orange-500 to-orange-600 shadow-tracq-card">
          <BookOpen className="w-10 h-10 text-white" />
        </div>
        <Stack align="center" className="w-full max-w-4xl" direction="col" gap={2}>
          <Heading className="font-bold text-center text-orange-400" level="h3" size="xl">
            Reading
          </Heading>
          <p className="text-[#CBD5E1] text-center text-lg w-full px-4">
            Read something inspiring or educational. Take notes on what resonates with you.
          </p>
        </Stack>
      </Stack>

      {/* Reading Notes Textarea */}
      <div className="space-y-3 w-full">
        <label className="block text-sm font-semibold text-[#CBD5E1]">Reading Notes</label>
        <Textarea
          className="bg-[#334155] border-[#475569] text-[#F1F5F9] placeholder:text-[#94A3B8] min-h-[200px] text-base leading-relaxed w-full"
          onChange={(value: string) => {
            onChange(value);
          }}
          placeholder="What did you read today? What insights did you gain? What quotes or ideas stood out to you? How will you apply what you learned?"
          rows={8}
          value={value}
        />
        <p className="text-[#94A3B8] text-xs">
          Capture the key insights and ideas that resonated with you during your reading.
        </p>
      </div>
    </div>
  );
};
